import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { useCalendarConnection } from '@/hooks/calendar-slots/use-calendar-connection';
import { useBusinessSlugCheck } from '@/hooks/use-business-slug-check';
import { useOnboarding } from '@/hooks/use-onboarding';
import type { Notification } from '@/lib/notifications/types';

interface RequiredAction {
  id: string;
  type: 'calendar_connection' | 'business_profile';
  title: string;
  message: string;
  action_url: string;
  action_label: string;
  is_completed: boolean;
}

export function useRequiredActions() {
  const { user } = useAuth();
  const { hasConnection, loading: calendarLoading } = useCalendarConnection();
  const { hasBusinessSlug, loading: businessSlugLoading } = useBusinessSlugCheck();
  const { hasCompletedOnboarding, loading: onboardingLoading } = useOnboarding();
  const [requiredActions, setRequiredActions] = useState<RequiredAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [onboardingCompletedAt, setOnboardingCompletedAt] = useState<Date | null>(null);

  // Fetch onboarding completion timestamp
  useEffect(() => {
    if (!user || !hasCompletedOnboarding) {
      setOnboardingCompletedAt(null);
      return;
    }

    const fetchOnboardingTimestamp = async () => {
      try {
        const { data, error } = await supabase
          .from('user_settings')
          .select('updated_at')
          .eq('user_id', user.id)
          .eq('completed_onboarding', true)
          .maybeSingle();

        if (error) {
          console.error('Error fetching onboarding timestamp:', error);
          return;
        }

        if (data?.updated_at) {
          setOnboardingCompletedAt(new Date(data.updated_at));
        }
      } catch (error) {
        console.error('Error fetching onboarding timestamp:', error);
      }
    };

    fetchOnboardingTimestamp();
  }, [user, hasCompletedOnboarding]);

  // Check if user recently completed onboarding (within 7 days)
  const isRecentlyCompletedOnboarding = useCallback(() => {
    if (!onboardingCompletedAt || !hasCompletedOnboarding) {
      return false;
    }

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    return onboardingCompletedAt > sevenDaysAgo;
  }, [onboardingCompletedAt, hasCompletedOnboarding]);

  // Create or update required action notifications
  const createOrUpdateNotification = useCallback(async (action: RequiredAction) => {
    if (!user) return;

    try {
      // Check if notification already exists
      const { data: existingNotifications } = await supabase
        .from('notifications')
        .select('id')
        .eq('user_id', user.id)
        .eq('type', 'required_action')
        .eq('data->>action_type', action.type)
        .limit(1);

      const notificationData = {
        user_id: user.id,
        type: 'required_action' as const,
        title: action.title,
        message: action.message,
        data: {
          action_type: action.type,
          is_required_action: true
        },
        is_read: false,
        action_url: action.action_url,
        action_label: action.action_label
      };

      if (existingNotifications && existingNotifications.length > 0) {
        // Update existing notification
        await supabase
          .from('notifications')
          .update(notificationData)
          .eq('id', existingNotifications[0].id);
      } else {
        // Create new notification
        await supabase
          .from('notifications')
          .insert(notificationData);
      }
    } catch (error) {
      console.error('Error creating/updating required action notification:', error);
    }
  }, [user]);

  // Mark required action as completed
  const markActionCompleted = useCallback(async (actionType: string) => {
    if (!user) return;

    try {
      // Mark all notifications for this action type as read
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('type', 'required_action')
        .eq('data->>action_type', actionType);
    } catch (error) {
      console.error('Error marking action as completed:', error);
    }
  }, [user]);

  // Check and manage required actions
  useEffect(() => {
    if (!user || calendarLoading || businessSlugLoading || onboardingLoading) return;

    const actions: RequiredAction[] = [];
    const recentlyCompleted = isRecentlyCompletedOnboarding();

    // For users who have completed onboarding, be more lenient with required actions
    // Don't show business profile as required if they just completed onboarding recently
    if (!hasBusinessSlug && !recentlyCompleted) {
      actions.push({
        id: 'business-profile',
        type: 'business_profile',
        title: 'Set Up Business Profile',
        message: 'Complete your business profile to customize your booking URLs and improve your booking experience.',
        action_url: '/settings',
        action_label: 'Set Business Profile',
        is_completed: false
      });
    }

    // Only show calendar connection as required if user hasn't completed onboarding recently
    // or if they haven't connected their calendar after onboarding
    if (!hasConnection && !recentlyCompleted) {
      actions.push({
        id: 'calendar-connection',
        type: 'calendar_connection',
        title: 'Connect Your Calendar',
        message: 'Connect your calendar to enable meeting link functionality and ensure accurate availability.',
        action_url: '/settings/calendar',
        action_label: 'Connect Calendar',
        is_completed: false
      });
    }

    setRequiredActions(actions);
    setLoading(false);

    // Create notifications for incomplete actions
    actions.forEach(action => {
      if (!action.is_completed) {
        createOrUpdateNotification(action);
      }
    });

    // Mark completed actions
    if (hasConnection) {
      markActionCompleted('calendar_connection');
    }
    if (hasBusinessSlug) {
      markActionCompleted('business_profile');
    }
  }, [user, hasConnection, hasBusinessSlug, hasCompletedOnboarding, onboardingCompletedAt, calendarLoading, businessSlugLoading, onboardingLoading, createOrUpdateNotification, markActionCompleted, isRecentlyCompletedOnboarding]);

  return {
    requiredActions,
    loading,
    hasIncompleteActions: requiredActions.length > 0
  };
} 